import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { imageSetAvatarThunk } from "../image/imageSetAvatar";
import { ObjectTarget } from "../../app/permissions";
import { studentInsert, studentUpdate } from "./studentInsUpdSlice";
import { ClassroomInfo } from "../classroom/classroomSlice";
import { UserInfo } from "../user/userSlice";

export enum StudentStatus {
    ENABLED = 0,
    DISABLED = 1,
}

export interface StudentState {
    state: LoadStateInfo;
    studentId?: string;
    selected?: StudentInfo;
}

const initialState: StudentState = {
    state: notStarted(),
};

export interface RetrieveClassroomRequest {
    studentId?: string;
}
export const retrieveStudent = createAsyncThunk(
    "retrieveStudent",
    async (param: RetrieveClassroomRequest, thunkAPI) => {
        console.debug("retrieve student");
        const response = await fetch(`${getBackendUrl()}/api/student/${param.studentId}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    }
);

export const studentSlice = createSlice({
    name: "student",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        clean: (state) => {
            state.state = notStarted();
            state.selected = undefined;
        }
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(retrieveStudent.pending, (state, action) => {
                state.state = loading();
                state.studentId = action.meta.arg.studentId;
                state.selected = undefined;
            })
            .addCase(retrieveStudent.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.selected = action.payload;
            })
            .addCase(retrieveStudent.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(imageSetAvatarThunk.fulfilled, (state, action) => {
                if (state.selected
                    && action.meta.arg.objectTarget == ObjectTarget.STUDENT
                    && action.meta.arg.targetId == state.selected?.id
                ) {
                    state.selected.url = action.payload;
                }
            })
            .addCase(studentInsert.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.selected = action.payload;
            })
            .addCase(studentUpdate.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.selected = action.payload;
            });
    },
});

export interface StudentInfo {
    id: string;
    schoolId: string;
    name: string;
    nickname?: string;
    url?: string;
    email?: string;
    birthDate?: string;
    schoolNumber?: string;
    freeText?: string;
    studentStatus?: StudentStatus;
    classrooms?: ClassroomInfo[];
    family?: UserInfo[];
}

export const studentDisplayName = (student: StudentInfo) => {
    return student.nickname || student.name || student.id;
};

// export const { } = studentSlice.actions;

export const selectStudent = (state: RootState) => state.student;

export default studentSlice.reducer;
